import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Task, uuidToColor } from '@repo/shared';

import { AggregateClusterResponse } from '@/api/generated.schemas';
import type { BarChartClickHandler } from '@/components/charts/types';
import { ClusterChart } from '@/routes/(app)/(clusters)/components/cluster-chart';
import type { ChartData } from '@/routes/(app)/(clusters)/lib/types';

interface Config {
  title: string;
  description: string;
  xAxis:
    | 'bleu'
    | 'comet'
    | 'wordsPerMinute'
    | 'wordErrorRate'
    | 'time'
    | 'cost'
    | 'cer'
    | 'wer'
    | 'structuralDistance';
}

interface ClusterChartsProps {
  onClick: BarChartClickHandler;
  data: AggregateClusterResponse;
}

export const ClusterCharts: FC<ClusterChartsProps> = ({ onClick, data }) => {
  const { t } = useTranslation();

  const chartData: Array<ChartData> = useMemo(
    () =>
      data.map((cluster) => ({
        // only include metrics that are numbers
        ...Object.fromEntries(
          Object.entries(cluster.metrics ?? {}).filter(
            ([_, value]) => typeof value === 'number'
          )
        ),
        fill: uuidToColor(cluster.id),
        id: cluster.id,
        model: cluster.model,
      })),
    [data]
  );

  const config: Record<Task, Array<Config>> = useMemo(
    () => ({
      [Task.TEXT_TRANSLATION]: [
        {
          title: t('metrics.bleu'),
          description: t('metrics.bleuDescription'),
          xAxis: 'bleu',
        },
        {
          title: t('metrics.comet'),
          description: t('metrics.cometDescription'),
          xAxis: 'comet',
        },
      ],
      [Task.GENERATE_AUDIO]: [
        {
          title: t('metrics.wordsPerMinute'),
          description: t('metrics.wordsPerMinuteDescription'),
          xAxis: 'wordsPerMinute',
        },
        {
          title: t('metrics.wordErrorRate'),
          description: t('metrics.wordErrorRateDescription'),
          xAxis: 'wordErrorRate',
        },
      ],
      [Task.EXTRACT_STRUCTURE]: [
        {
          title: t('metrics.cer'),
          description: t('metrics.cerDescription'),
          xAxis: 'cer',
        },
        {
          title: t('metrics.wer'),
          description: t('metrics.werDescription'),
          xAxis: 'wer',
        },

        {
          title: t('metrics.structuralDistance'),
          description: t('metrics.structuralDistanceDescription'),
          xAxis: 'structuralDistance',
        },
      ],
      [Task.DESCRIBE_IMAGE]: [],
    }),
    [t]
  );

  const charts = useMemo(
    () => (data[0]?.metrics?.task ? config[data[0].metrics.task as Task] : []),
    [data, config]
  );

  return (
    <div className='**:data-chart:w-[400px] flex flex-wrap gap-6'>
      {charts.map((chart) => (
        <ClusterChart
          key={chart.xAxis}
          title={chart.title}
          description={chart.description}
          xAxis={chart.xAxis}
          data={chartData}
          onClick={onClick}
        />
      ))}
    </div>
  );
};
